import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import { Download, ChevronDown, ChevronUp } from "lucide-react";
import { fetchParticipantsByLocation } from "../../redux/Actions/ChatActions";
import { RootState } from "../../redux/store";

const ContactManagement = () => {
    const dispatch = useDispatch();
    const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});
    
    // Get participants data from Redux store
    const { participantsByLocation, loading, error } = useSelector((state: RootState) => ({
        participantsByLocation: state.chat.participantsByLocation,
        loading: state.chat.loading,
        error: state.chat.error
    }));

    useEffect(() => {
        // Fetch participants by location when component mounts
        dispatch(fetchParticipantsByLocation({ isShowLoader: true }) as any);
    }, [dispatch]);

    const toggleCountry = (country: string) => {
        setExpandedCountries(prev => ({
            ...prev,
            [country]: !prev[country]
        }));
    };

    const exportContacts = () => {
        if (!participantsByLocation) return;
        
        const csvContent = [
            "Type,Name,Email,Phone,WhatsApp,Country,Province",
            ...Object.entries(participantsByLocation).flatMap(([country, provinces]) =>
                Object.entries(provinces).flatMap(([province, participants]) => 
                    participants.map(p => `${p.role.name},"${p.firstName} ${p.lastName}","${p.email}","${p.phone}","${p.whatsappNumber}","${country}","${province}"`)
                )
            )
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", "contacts_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Show loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading contacts...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="text-red-600">Error loading contacts: {error}</p>
                    <Button 
                        onClick={() => dispatch(fetchParticipantsByLocation({ isShowLoader: true }) as any)}
                        className="mt-4"
                    >
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    // Show empty state
    if (!participantsByLocation || Object.keys(participantsByLocation).length === 0) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="text-gray-600">No contacts found</p>
                    <Button 
                        onClick={() => dispatch(fetchParticipantsByLocation({ isShowLoader: true }) as any)}
                        className="mt-4"
                    >
                        Refresh
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" style={{ textAlign: "left" }}>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                    <h2 className="text-lg font-semibold">Contact Management</h2>
                    <p className="text-gray-600 mt-1">View and export customer and professional contacts</p>
                </div>
                <Button onClick={exportContacts} className="flex items-center gap-2 w-full sm:w-auto">
                    <Download className="h-4 w-4" />
                    Export Contacts
                </Button>
            </div>

            {Object.entries(participantsByLocation).map(([country, provinces]) => (
                <Card key={country} className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">{country}</h3>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleCountry(country)}
                        >
                            {expandedCountries[country] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </div>
                    {expandedCountries[country] && (
                        <div className="space-y-4">
                            {Object.entries(provinces).map(([province, participants]) => (
                                <div key={province}>
                                    <h4 className="font-medium mb-2">{province}</h4>
                                    <div className="space-y-2">
                                        {participants.map((participant, i) => (
                                            <div key={`participant-${i}`} className="flex flex-col sm:flex-row gap-2 text-sm">
                                                <span className="font-medium w-24">{participant.role.name}:</span>
                                                <span>
                                                    {participant.firstName} {participant.lastName} | 
                                                    {participant.email} | 
                                                    {participant.phone} | 
                                                    WhatsApp: {participant.whatsappNumber}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
};

export default ContactManagement;